import React, { useRef } from "react";
import { Modal, Box, IconButton, Button, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import html2canvas from "html2canvas";
import { MemoryThankYouCard } from "./MemoryThankYouCard";

interface DownloadGraphicModalProps {
  open: boolean;
  onClose: () => void;
  deceasedName: string;
  backgroundImage: string;
  logoImage: string;
}

export const DownloadGraphicModal: React.FC<DownloadGraphicModalProps> = ({
  open,
  onClose,
  deceasedName,
  backgroundImage,
  logoImage,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);

  const handleDownload = async () => {
    if (!cardRef.current) return;

    try {
      const canvas = await html2canvas(cardRef.current, {
        width: 1080,
        height: 1080,
        scale: 1,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const link = document.createElement("a");
      link.download = `memory-${deceasedName.replace(/\s+/g, "_")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("❌ Error generating PNG:", err);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute" as const,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          outline: "none",
          width: "auto",
          minWidth: 500,
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: -8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>

        {/* PREVIEW - scaled down visually */}
        <Box
          sx={{
            width: 486,
            height: 486,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              transform: "scale(0.45)",
              transformOrigin: "top left",
              position: "absolute",
            }}
          >
            <MemoryThankYouCard
              deceasedName={deceasedName}
              backgroundImage={backgroundImage}
              logoImage={logoImage}
            />
          </Box>
        </Box>

        {/* HIDDEN full-size card for download — inside DOM, but invisible */}
        <Box
          sx={{
            width: 0,
            height: 0,
            overflow: "hidden",
          }}
        >
          <Box ref={cardRef} sx={{ width: 1080, height: 1080 }}>
            <MemoryThankYouCard
              deceasedName={deceasedName}
              backgroundImage={backgroundImage}
              logoImage={logoImage}
            />
          </Box>
        </Box>

        {/* Download Button */}
        <Stack direction="row" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download Graphic
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};
